import { useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { PlatformIcon } from '@/components/common/PlatformIcon';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/utils/formatters';
import type { UserDevice } from '@/types/user.types';

const VISIBLE_DEFAULT = 3;

function deviceName(device: UserDevice): string {
  return device.model || device.platform || 'Aparelho sem identificação';
}

function deviceSpecs(device: UserDevice): string {
  const system = device.platform
    ? `${device.platform}${device.osVersion ? ` ${device.osVersion}` : ''}`
    : device.osVersion
      ? `Sistema ${device.osVersion}`
      : 'Sistema desconhecido';
  const app = device.appVersion ? `app ${device.appVersion}` : 'versão do app desconhecida';
  return `${system} · ${app}`;
}

/** Aparelhos do usuário (mais recente primeiro), com versão do app/sistema e push. */
export function UserDevicesSection({ devices }: { devices: UserDevice[] }) {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? devices : devices.slice(0, VISIBLE_DEFAULT);
  const hidden = devices.length - VISIBLE_DEFAULT;

  return (
    <section>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        Aparelhos {devices.length > 0 && `(${devices.length})`}
      </h3>

      {devices.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-text-secondary">
          Nenhum aparelho registrado.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
          <ul className="divide-y divide-border">
            {visible.map((device, i) => (
              <li key={device.id} className="flex items-center gap-3 px-4 py-3">
                <span
                  className={cn(
                    'flex size-9 shrink-0 items-center justify-center rounded-lg',
                    i === 0 ? 'bg-primary/10' : 'bg-muted'
                  )}
                >
                  <PlatformIcon platform={device.platform} size={16} colored />
                </span>

                <div className="min-w-0 flex-1">
                  <p className="flex items-center gap-2 truncate text-sm font-medium text-text-title">
                    {deviceName(device)}
                    {i === 0 && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                        mais recente
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-text-secondary">{deviceSpecs(device)}</p>
                </div>

                <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-text-secondary">
                  <span title={`Primeiro acesso: ${formatDateTime(device.firstSeen)}`}>
                    {formatDateTime(device.lastSeen)}
                  </span>
                  <span
                    className={cn(
                      'inline-flex items-center gap-1',
                      device.pushEnabled ? 'text-success' : 'text-text-hint'
                    )}
                  >
                    {device.pushEnabled ? <Bell size={12} /> : <BellOff size={12} />}
                    {device.pushEnabled ? 'push ativo' : 'sem push'}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="w-full border-t border-border px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-muted/50"
            >
              {showAll ? 'Mostrar menos' : `Ver todos (+${hidden})`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
